import { FC, useContext, useEffect } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { Link, useRouteMatch } from "react-router-dom";

import { Button } from "components";
import AddedRoyaltyAccountsChecklist, {
  useAddedAccountsChecklistInfo,
} from "containers/AddedRoyaltyAccountsChecklist";
import { AppContext } from "contexts";
import * as routes from "routes";
import { useRoyaltyPayors } from "services/royaltyPayor";
import { getCurrentUser } from "services/users";
import theme from "theme";

import * as addRoyaltyAccountRoutes from "../routes";
import StarIcon from "./check-star.svg?react";
import StepContainer from "./StepContainer";

const useStyles = makeStyles(() =>
  createStyles({
    heading: {
      fontSize: "1.2rem",
      fontWeight: theme.typography.fontWeightMedium,
    },
    icon: {
      marginBottom: theme.spacing(2),
    },
  }),
);

const AddRoyaltyAccountSuccess: FC = () => {
  const classes = useStyles();

  const valuationPayorMatch = useRouteMatch<{
    royaltyPayorName: string;
  }>(addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccountSuccess());
  const credentialsPayorMatch = useRouteMatch<{
    royaltyPayorName: string;
  }>(addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccountSuccess());
  const match = valuationPayorMatch ?? credentialsPayorMatch;
  const { royaltyPayorName } = match?.params ?? {};
  const { data: royaltyPayors } = useRoyaltyPayors();
  const royaltyPayor = royaltyPayors?.find(
    royaltyPayor => royaltyPayor.name === royaltyPayorName,
  );
  const { isChecklistComplete } = useAddedAccountsChecklistInfo();

  // if we reach this step and the user has connected their first asset then we need to update the user object in order to update `canSeeSellerDashboard`
  const { user, setUser } = useContext(AppContext);
  useEffect(() => {
    if (!user.canSeeSellerDashboard) {
      const updateUser = async () => {
        setUser(await getCurrentUser());
      };
      updateUser();
    }
  }, [user, setUser]);
  return (
    <Box maxWidth="500px" margin="auto">
      <StepContainer>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gridGap={theme.spacing(2)}
        >
          <StarIcon className={classes.icon} />
          <Typography variant="h2" className={classes.heading}>
            {royaltyPayor?.display_name ?? "Other Royalty Account"} Successfully
            Added
          </Typography>
          {isChecklistComplete ? (
            <Typography variant="h5" className={classes.heading} align="center">
              Checklist Complete
            </Typography>
          ) : (
            <Typography align="center">
              Add another royalty account to increase the value of your deal
              options.
            </Typography>
          )}
          <Box>
            <AddedRoyaltyAccountsChecklist removeBoxShadow />
          </Box>
          <Box maxWidth="370px">
            {isChecklistComplete ? (
              <>
                <Box mb={2}>
                  <Typography align="center">
                    We’ll be in touch to discuss your deal options. In the
                    meantime, read about our deal option types and how the
                    process works.
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={routes.sellerAboutDealOptions()}
                  variant="contained"
                  customColor="green"
                  fullWidth
                >
                  About Deal Options
                </Button>
                <Box my={2}>
                  <Typography align="center">
                    Have more accounts to connect? Continue to connect more
                    accounts and increase your deal potential.
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={routes.sellerAddRoyaltyAccount()}
                  variant="outlined"
                  customColor="green"
                  fullWidth
                >
                  Continue Adding Accounts
                </Button>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to={routes.sellerAddRoyaltyAccount()}
                  variant="contained"
                  customColor="green"
                  fullWidth
                >
                  Continue Adding Accounts
                </Button>
                <Box my={2}>
                  <Typography align="center">
                    If you have added all of your royalty accounts and want to
                    learn more about our deal options, click below.
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={routes.sellerAboutDealOptions()}
                  variant="outlined"
                  customColor="green"
                  fullWidth
                >
                  About Deal Options
                </Button>
              </>
            )}
          </Box>
        </Box>
      </StepContainer>
    </Box>
  );
};

export default AddRoyaltyAccountSuccess;
